import { useMemo } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useLazyLoad } from '../../../hooks/use-lazy-load';
import { useLazyGetUserPosts } from '../../../redux/api/postsApi';
import { useGetMyDetailsDetailed } from '../../../redux/api/users/usersApi';
import { AvatarSize } from '../../../shared/constants';
import {
  communitiesFromCommunitySubscriptionRelations,
  usersFromSubscriberRelations,
  usersFromSubscriptionRelations,
} from '../../../utils/collections/relation-entities';

const desktopRelationsItemsCount = 5;
const mobileRelationAvatarSize = AvatarSize.s;
const mobileRelationItemPaddingX = 8;
const mobileRelationItemGap = 4;

export function useHomePage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const {
    data: user,
    isFetching: isFetchingUser,
    isError: isUserError,
    error: userError,
  } = useGetMyDetailsDetailed();
  const userSubscriptions = usersFromSubscriptionRelations(user?.subscriptions);
  const userSubscribers = usersFromSubscriberRelations(user?.subscribers);
  const communities = communitiesFromCommunitySubscriptionRelations(
    user?.communitySubscriptions,
  );
  const userId = user?.id ?? '';
  const {
    items: posts,
    footerRef,
    refetchAll,
    isFetching: isFetchingPosts,
    error: postsError,
  } = useLazyLoad(
    useLazyGetUserPosts,
    { userId },
    (items) => ({
      timestamp: items?.length
        ? new Date(items[items.length - 1]?.createdAt).valueOf()
        : new Date().valueOf() + 5000,
    }),
    !userId,
  );

  const isUserLoading = isFetchingUser && !user?.id;
  const isRelationPanel =
    !!userSubscribers?.length ||
    !!userSubscriptions?.length ||
    !!communities?.length;
  const showNoResult = !!posts && !posts.length && !isFetchingPosts;
  const visibleRelationsCount = useMemo(() => {
    if (isMdUp) {
      return desktopRelationsItemsCount;
    }

    const viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : undefined;
    const mobileRelationItemWidth =
      mobileRelationAvatarSize + mobileRelationItemPaddingX;
    const mobileVisibleRelationsCount = Math.floor(
      ((viewportWidth ?? mobileRelationItemWidth) + mobileRelationItemGap) /
        (mobileRelationItemWidth + mobileRelationItemGap),
    );

    return Math.max(1, mobileVisibleRelationsCount);
  }, [isMdUp]);

  return {
    user,
    communities,
    userSubscribers,
    userSubscriptions,
    posts,
    footerRef,
    refetchAll,
    isFetchingPosts,
    postsError,
    isUserLoading,
    isRelationPanel,
    showNoResult,
    visibleRelationsCount,
    isUserError,
    userError,
  };
}
